"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { AppContext } from "@/App"
import { Button } from "@/Components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Textarea } from "@/Components/ui/textarea"

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import DatePicker from "@/Components/ui/DatePicker"

import { Action } from "@/Components/Actions/Action";
import React from "react"
import { WizardContext } from "@/Components/wizard/wizardProvider"
import { useIsMobile } from "@/hooks/use-mobile";

const formSchema = z.object({
    date: z.date(),
    workstream: z
        .string({
            required_error: "Please select a Workstream.",
        }),
    workPackage: z
        .string({
            required_error: "Please select a work package.",
        }),
    raisedBy: z.string(),
    description: z
        .string()
        .min(1, {
            message: "Action must be at least 1 character.",
        })
        .max(250, {
            message: "Action must not be longer than 250 characters.",
        }),
    owner: z.string(),
    dueDate: z.date(),
    status: z.enum(["Open", "Closed", "Cancelled"]),
    notes: z.string().max(500),
})

interface ActionFormProps {
    action?: Action,
    saveAction: (action: Action) => void,
}

function ActionForm({ action, saveAction }: ActionFormProps) {
    const isMobile = useIsMobile();
    const app = React.useContext(AppContext);
    const wizard = React.useContext(WizardContext);
    // team options is all team members and project managers
    const teamOptions = app.projectManagers?.concat(app.teamMembers).concat(app.sponsors) || [];
    const [descriptionLength, setDescriptionLength] = React.useState(action ? action.description.length : 0);
    const [notesLength, setNotesLength] = React.useState(action ? action.notes?.length ?? 0 : 0);

    // 1. Define your form.
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            date: action ? new Date(action.date) : new Date(),
            workstream: action ? action.workstream : (app.selectedProject?.projectTypeId === 1 ? app.workPackageOptions?.[0]?.workstreamName : ""),
            workPackage: action ? action.workPackage : "",
            raisedBy: action ? action.raisedBy : "",
            description: action ? action.description : "",
            owner: action ? action.userId === "" ? "unassigned" : action.userId : "unassigned",
            dueDate: action ? new Date(action.dueDate) : new Date(new Date().setDate(new Date().getDate() + 7)),
            status: action ? action.status : "Open",
            notes: action ? action.notes : "",
        },
        mode: "onBlur",
    })

    React.useEffect(() => {
        if (wizard.showWizardPointer && wizard.currentStep?.sendClick) {
            switch (wizard.currentStep.stepComponentId) {
                case "workPackageSelect":
                    form.setValue("workPackage", "WP1");
                    break;
                case "statusSelect":
                    form.setValue("status", "Open");
                    break;
                case "ownerSelect":
                    form.setValue("owner", teamOptions[0]?.id as string || "unassigned");
                    break;
                case "raisedBySelect":
                    form.setValue("raisedBy", teamOptions[0]?.displayName || "");
                    break;
                case "actionDescription":
                    form.setValue("description", "This is a sample action description added during the guided tour.");
                    setDescriptionLength(66);
                    break;
                case "actionNotes":
                    form.setValue("notes", "These are sample notes added during the guided tour.");
                    setNotesLength(44);
                    break;
                case "submitActionButton":
                    document.getElementById("submitActionButton")?.click();
                    break;
                default:
                    break;
            }
        }
    }, [wizard.showWizardPointer, wizard.currentStep, form]);

    // 2. Define a submit handler.
    function onSubmit(data: z.infer<typeof formSchema>) {

        const newAction = {
            projectId: app.selectedProject?.id || 0,
            actionId: action ? action.actionId : 0,
            date: new Date(data.date),
            workstream: app.workPackageOptions?.[0]?.workstreamName || "",
            workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
            workPackageId: app.workPackageOptions?.find((ws) => ws.workstreamName === data.workstream)?.workPackageNames
                .find((wp) => wp.workPackageName === data.workPackage)?.workPackageId || 0,
            workPackage: data.workPackage,
            raisedBy: data.raisedBy,
            description: data.description,
            owner: teamOptions.find(t => t.id == app.applicationUser?.id)?.displayName ?? "Unassigned",
            userId: data.owner == "unassigned" ? "" : data.owner,
            dueDate: new Date(data.dueDate),
            status: data.status as "Closed" | "Cancelled" | "Open",
            notes: data.notes,
            seen: false,
            acknowledged: false,
        }

        saveAction(newAction);

        if (action == null || action == undefined) {
            form.setValue("description", "");
            form.setValue("notes", "");
            setDescriptionLength(0);
            setNotesLength(0);

            setTimeout(() => form.setFocus("description"), 0);
        }
    }

    if (isMobile) {
        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4">
                    <FormField
                        control={form.control}
                        name="workPackage"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Work Package</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="text-2xl w-full p-2">
                                            <SelectValue placeholder="Select a work package" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {app.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                            <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem className="flex flex-col w-full">
                                    <FormLabel className="text-2xl">Date</FormLabel>
                                    <DatePicker date={field.value} setDate={field.onChange} className="text-2xl" />
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Action</div>
                                    <div><span className="text-lg flex text-gray-500 items-end pr-2">{descriptionLength}/250</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Action description"
                                        className="resize-none text-2xl"
                                        {...field}
                                        rows={4}
                                        maxLength={250}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setDescriptionLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-col space-y-4">
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem className="w-full">
                                    <FormLabel className="text-2xl">Raised By</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl">
                                                <SelectValue placeholder="Select user" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {teamOptions?.map((user) => (
                                                <SelectItem key={user.id} value={user.displayName}>{user.displayName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="owner"
                            render={({ field }) => (
                                <FormItem className="w-full">
                                    <FormLabel className="text-2xl">Owner</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl">
                                                <SelectValue placeholder="Select user" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="unassigned">Unassigned</SelectItem>
                                            {teamOptions?.map((user) => (
                                                <SelectItem key={user.id} value={user.id as string}>{user.displayName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-col space-y-4">
                        <FormField
                            control={form.control}
                            name="dueDate"
                            render={({ field }) => (
                                <FormItem className="flex flex-col w-full">
                                    <FormLabel className="text-2xl">Due Date</FormLabel>
                                    <DatePicker date={field.value} setDate={field.onChange} className="text-2xl" />
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem className="w-full">
                                    <FormLabel className="text-2xl">Status</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl">
                                                <SelectValue placeholder="Select a status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Notes</div>
                                    <div><span className="text-lg flex text-gray-500 items-end pr-2">{notesLength}/500</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Optional notes"
                                        className="resize-none text-2xl"
                                        {...field}
                                        rows={3}
                                        maxLength={500}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <Button type="submit" className="w-full text-2xl py-6">Submit</Button>
                </form>
            </Form>
        );
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="flex flex-col space-y-4">
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="workPackage"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Work Package</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger id="workPackageSelect" className="w-48 p-2">
                                                <SelectValue placeholder="Select a work package" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {app.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                                <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger id="statusSelect" className="w-48 p-2">
                                                <SelectValue placeholder="Select a status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Date</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="owner"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Owner</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger id="ownerSelect" className="w-48 p-2">
                                                    <SelectValue placeholder="Owner..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Raised By</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger id="raisedBySelect" className="w-48 p-2">
                                                    <SelectValue placeholder="Who raised the action..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="dueDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Due Date</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={field.value} setDate={(date) => field.onChange(date)} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Action</div><div><span className="text-xs flex text-gray-500 items-end pr-2">{descriptionLength}/250</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your action..."
                                        className="resize-none"
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setDescriptionLength(e.target.value.length);
                                        }}
                                        rows={3}
                                        id="actionDescription"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Notes </div><div><span className="text-xs flex text-gray-500 items-end pr-2">{notesLength}/500</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes..."
                                        className="resize-none"
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                        rows={6}
                                        id="actionNotes"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row ml-auto space-x-4">
                        <Button id="submitActionButton" type="submit" className="w-48">Submit</Button>
                    </div>
                </div>
            </form>
        </Form>
    )
}

export default ActionForm;
